import React, { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Form, Formik, FormikValues } from "formik";

import { bemClassName } from "../../utils/bemClassName";
import composeFieldValues from "./composeFieldValues";
import { joinClassNames } from "../../utils/joinClassNames";
import {
    useSearchQuestion,
    useSearchResults,
} from "../../context/DataContext";
import Facets from "./Facets";
import FacetToggle from "./FacetToggle";
import QueryField from "./QueryField";
import SearchFormContext from "../../context/SearchFormContext";
import SearchSubmitIntentContext from "./SearchSubmitIntentContext";

import "./SearchForm.css";
import styles from "./SearchForm.module.css";
import composeFormikValues from "./composeFormikValues";
import composeFormURLSearchParams from "./composeFormURLSearchParams";
import shouldUseQueryRedirect, {
    SearchSubmitIntent,
} from "./shouldUseQueryRedirect";

export type CommonField = {
    disabled?: boolean;
    id: string;
    label: string;
    name: string;
    placeholder?: string;
};

export type SearchFieldFacetType =
    | "checkboxGroup"
    | "radioGroup"
    | "select"
    | "dateRange"
    | "multiSelect";

export type SearchFieldInput = CommonField & {
    type?: string;
};

export type SearchFieldOption = {
    id: string;
    label: string;
    value: string;
};

export type SearchFieldSelect = CommonField & {
    facetType: "select";
    options: SearchFieldOption[];
};

export type SearchFieldMultiSelect = CommonField & {
    facetType: "multiSelect";
    options: SearchFieldOption[];
};

export type SearchFieldCheckbox = CommonField & {
    facetType: "checkboxGroup";
    options: SearchFieldOption[];
};

export type SearchFieldRange = CommonField & {
    facetType: "dateRange";
    options: SearchFieldOption[];
    idStart?: string;
    idEnd?: string;
    nameStart?: string;
    nameEnd?: string;
};

export type SearchFieldRadio = CommonField & {
    facetType: "radioGroup";
    options: SearchFieldOption[];
};

export type SearchFieldsType = {
    query: SearchFieldInput;
    // Facets
    archived?: SearchFieldCheckbox;
    datePublished?: SearchFieldCheckbox;
    regionLocation?: SearchFieldSelect;
    resourceType?: SearchFieldSelect;
    status?: SearchFieldSelect;
    topic?: SearchFieldSelect;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
};

export interface SearchFormInitialValues {
    [key: string]: string | string[] | null | undefined;
    // Funnelback built-in params
    query: string;
    // Funnelback facets
    archived?: string[];
    datePublished?: string[];
    regionLocation?: string;
    resourceType?: string;
    topic?: string;
}

export interface SearchFormProps {
    facetBlockId: string;
    formAction: string;
    initialExpandFacets: boolean;
}

export const SearchForm = ({
    facetBlockId,
    formAction,
    initialExpandFacets,
}: SearchFormProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [expandFacets, setExpandFacets] = useState(initialExpandFacets);
    const submitIntentRef = useRef<SearchSubmitIntent>("facets");
    const setSubmitIntent = (intent: SearchSubmitIntent) => {
        submitIntentRef.current = intent;
    };
    const { facets, allFacets, config } = useSearchResults();
    const { setFormQuestion } = useSearchQuestion();
    const facetProps = { id: facetBlockId };
    const facetToggleProps = { expandFacets, facetBlockId, setExpandFacets };
    const maybeFacets = expandFacets ? <Facets {...facetProps} /> : null;

    const handleSearch = (formValues: FormikValues): void => {
        // Use current input value so URL and search stay in sync when typing then Enter
        const queryInput =
            typeof document !== "undefined"
                ? document.querySelector<HTMLInputElement>(
                      'input[name="query"]'
                  )
                : null;
        const queryFromInput = (queryInput?.value ?? "").trim();
        const formValuesWithInput = {
            ...formValues,
            query:
                queryFromInput !== ""
                    ? queryFromInput
                    : (formValues.query ?? "").trim(),
        };

        const fullUrl =
            typeof window !== "undefined" ? window.location.href : "";
        const submitIntent = submitIntentRef.current;
        submitIntentRef.current = "facets";
        const redirectUrl = shouldUseQueryRedirect(
            submitIntent,
            fullUrl,
            formValuesWithInput.query
        );
        if (redirectUrl && typeof window !== "undefined") {
            window.location.href = redirectUrl;
            return;
        }

        const submittedSearchParams = composeFormURLSearchParams(
            formValuesWithInput,
            allFacets,
            config.collection
        );

        const facetParams: { [type: string]: string | string[] } = {};
        submittedSearchParams.forEach((paramValue, paramKey) => {
            // Do not submit unpopulated facets
            if (!paramValue.trim()) return;

            if (paramKey in facetParams) {
                const previousValue = facetParams[paramKey];
                if (typeof previousValue === "string") {
                    facetParams[paramKey] = [previousValue, paramValue];
                } else {
                    facetParams[paramKey] = [...previousValue, paramValue];
                }
            } else {
                facetParams[paramKey] = paramValue;
            }
        });

        // Request new data from Funnelback
        setFormQuestion(formValuesWithInput.query, facetParams);
        // Set URL search params so address bar shows current query
        setSearchParams(submittedSearchParams);
    };

    const fieldsConfig = composeFieldValues(allFacets);

    const initialFromRouter = composeFormikValues(searchParams, facets);
    const queryFromWindow =
        typeof window !== "undefined"
            ? new URLSearchParams(window.location.search).get("query")?.trim()
            : null;
    const initialValues = {
        ...initialFromRouter,
        ...(queryFromWindow != null && queryFromWindow !== ""
            ? { query: queryFromWindow }
            : {}),
    };
    const formConfig = { initialValues };
    const queryFromUrl = searchParams.get("query") ?? queryFromWindow ?? "";

    const maybeForm =
        formConfig && !!fieldsConfig ? (
            <SearchFormContext.Provider value={fieldsConfig}>
                <SearchSubmitIntentContext.Provider value={{ setSubmitIntent }}>
                    <div
                    className={joinClassNames([
                        bemClassName("find-a-panel", "search-bar"),
                        styles.formWrapper,
                    ])}
                >
                    <Formik
                        key={queryFromUrl}
                        {...formConfig}
                        enableReinitialize
                        onSubmit={handleSearch}
                    >
                        {({ submitForm }) => (
                            <Form
                                method="GET"
                                action={formAction}
                                className={styles.form}
                                onKeyDown={(e) => {
                                    if (e.key !== "Enter") return;
                                    const target = e.target as HTMLElement;
                                    if (
                                        target.getAttribute?.("name") !==
                                        "query"
                                    )
                                        return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSubmitIntent("query");
                                    setTimeout(() => submitForm(), 0);
                                }}
                            >
                                <div className={styles.searchBarRow}>
                                    <QueryField />
                                    {facets.length > 0 ? (
                                        <div className={styles.facetToggleSlot}>
                                            <FacetToggle
                                                {...facetToggleProps}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                                {maybeFacets}
                            </Form>
                        )}
                    </Formik>
                    <hr className={styles.separator} />
                </div>
                </SearchSubmitIntentContext.Provider>
            </SearchFormContext.Provider>
        ) : null;

    return maybeForm;
};

export default SearchForm;
